import React from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl,
  InputLabel, MenuItem, Select, Stack, Typography,
} from '@mui/material';
import {
  StargraveAdvancementChoice, StargraveCrewData, StargraveImprovementType,
  StargraveLeaderRole, StargraveStat,
} from '../../types';
import { stargravePowers } from '../../data/stargrave/coreRules';
import {
  canAdvanceStargraveLeader,
  getStargraveImprovementOptions,
} from '../../game/stargraveCampaignRules';

interface StargraveAdvanceLeaderModalProps {
  open: boolean;
  crewData: StargraveCrewData;
  experience: number;
  onClose: () => void;
  onAdvance: (role: StargraveLeaderRole, choice: StargraveAdvancementChoice) => void;
}

const statLabels: Record<Exclude<StargraveStat, 'armour'>, string> = {
  move: 'Move', fight: 'Fight', shoot: 'Shoot', will: 'Will', health: 'Health',
};
const statMaximums = { move: 7, fight: 6, shoot: 6, will: 8, health: 25 };

export const StargraveAdvanceLeaderModal: React.FC<StargraveAdvanceLeaderModalProps> = ({
  open,
  crewData,
  experience,
  onClose,
  onAdvance,
}) => {
  const [role, setRole] = React.useState<StargraveLeaderRole>('captain');
  const [type, setType] = React.useState<StargraveImprovementType | ''>('');
  const [value, setValue] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const captainLevel = crewData.captain?.level ?? 0;
  const firstMateLevel = crewData.firstMate?.level ?? 0;
  const leader = role === 'captain' ? crewData.captain : crewData.firstMate;
  const options = leader ? getStargraveImprovementOptions(leader.level + 1) : [];

  React.useEffect(() => {
    if (!open) return;
    const preferredRole: StargraveLeaderRole = crewData.captain
      && canAdvanceStargraveLeader('captain', captainLevel, firstMateLevel) ? 'captain' : 'firstMate';
    setRole(preferredRole);
    setType('');
    setValue('');
    setError(null);
  }, [open, crewData.captain, captainLevel, firstMateLevel]);

  React.useEffect(() => {
    setType('');
    setValue('');
  }, [role]);

  const selectedType = options.length === 1 ? options[0] : type;
  const activationPowers = leader?.powers.filter((power) => power.activation > 4) ?? [];
  const newPowers = stargravePowers.filter((power) => !leader?.powers.some((known) => known.powerId === power.id));
  const statOptions = (Object.keys(statLabels) as Array<Exclude<StargraveStat, 'armour'>>)
    .filter((stat) => leader && leader.stats[stat] < statMaximums[stat]);

  const handleAdvance = () => {
    if (!leader || !selectedType || !value) return;
    try {
      onAdvance(role, selectedType === 'stat'
        ? { type: selectedType, stat: value as Exclude<StargraveStat, 'armour'> }
        : { type: selectedType, powerId: value });
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to advance leader.');
    }
  };

  return <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>Spend Experience</DialogTitle>
    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
      {error && <Alert severity="error">{error}</Alert>}
      <Typography color="text.secondary">Available: {experience} XP | Cost: 100 XP</Typography>
      <FormControl fullWidth>
        <InputLabel id="stargrave-advance-leader">Leader</InputLabel>
        <Select labelId="stargrave-advance-leader" label="Leader" value={role}
          onChange={(event) => setRole(event.target.value as StargraveLeaderRole)}>
          <MenuItem value="captain" disabled={!crewData.captain || !canAdvanceStargraveLeader('captain', captainLevel, firstMateLevel)}>
            Captain{crewData.captain ? `: ${crewData.captain.name} (Level ${crewData.captain.level})` : ' unavailable'}
          </MenuItem>
          <MenuItem value="firstMate" disabled={!crewData.firstMate || !canAdvanceStargraveLeader('firstMate', captainLevel, firstMateLevel)}>
            First Mate{crewData.firstMate ? `: ${crewData.firstMate.name} (Level ${crewData.firstMate.level})` : ' unavailable'}
          </MenuItem>
        </Select>
      </FormControl>

      {leader && <Typography variant="h6">Level {leader.level} → {leader.level + 1}</Typography>}
      {options.length > 1 && <FormControl fullWidth>
        <InputLabel id="stargrave-improvement-type">Improvement</InputLabel>
        <Select labelId="stargrave-improvement-type" label="Improvement" value={type}
          onChange={(event) => { setType(event.target.value as StargraveImprovementType); setValue(''); }}>
          <MenuItem value="stat">Improve a Stat</MenuItem>
          <MenuItem value="newPower">Learn a New Power</MenuItem>
        </Select>
      </FormControl>}
      {options.length === 1 && <Typography color="text.secondary">
        Improvement: {options[0] === 'activation' ? 'Lower an Activation Number' : options[0] === 'stat' ? 'Improve a Stat' : 'Learn a New Power'}
      </Typography>}

      {selectedType === 'stat' && <FormControl fullWidth>
        <InputLabel id="stargrave-stat-improvement">Stat</InputLabel>
        <Select labelId="stargrave-stat-improvement" label="Stat"
          value={statOptions.includes(value as Exclude<StargraveStat, 'armour'>) ? value : ''}
          onChange={(event) => setValue(event.target.value)}>
          {statOptions.map((stat) => <MenuItem key={stat} value={stat}>{statLabels[stat]} ({leader?.stats[stat]} → {(leader?.stats[stat] ?? 0) + 1})</MenuItem>)}
        </Select>
      </FormControl>}
      {selectedType === 'activation' && <FormControl fullWidth>
        <InputLabel id="stargrave-activation-improvement">Power</InputLabel>
        <Select labelId="stargrave-activation-improvement" label="Power"
          value={activationPowers.some((power) => power.powerId === value) ? value : ''}
          onChange={(event) => setValue(event.target.value)}>
          {activationPowers.map((power) => <MenuItem key={power.powerId} value={power.powerId}>
            {stargravePowers.find((candidate) => candidate.id === power.powerId)?.name}: {power.activation} → {power.activation - 1}
          </MenuItem>)}
        </Select>
      </FormControl>}
      {selectedType === 'newPower' && <FormControl fullWidth>
        <InputLabel id="stargrave-new-power">Power</InputLabel>
        <Select labelId="stargrave-new-power" label="Power"
          value={newPowers.some((power) => power.id === value) ? value : ''}
          onChange={(event) => setValue(event.target.value)}>
          {newPowers.map((power) => <MenuItem key={power.id} value={power.id}>{power.name}</MenuItem>)}
        </Select>
      </FormControl>}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button variant="contained" disabled={!leader || !selectedType || !value || experience < 100} onClick={handleAdvance}>Advance</Button>
    </DialogActions>
  </Dialog>;
};
